import { DateTime } from "luxon";

/*
Dreambound business hours are 7am - 6pm PT every day except holidays.
- BW 2022-08-10
 */
const BUSINESS_HOURS_START = 7; // 7am PT
const BUSINESS_HOURS_END = 18; // 6pm PT
const BUSINESS_HOURS_TIMEZONE = "America/Los_Angeles";

/**
 * List of future holiday dates, as ISODate strings, where Dreambound is closed.
 *
 * Last updated and by:
 * - BW 2022-10-11
 * Good thru Oct 2023
 */
export const BUSINESS_HOURS_HOLIDAYS: [string, string][] = [
  ["2023-05-29", "Memorial Day"], // USA Memorial Day
  ["2023-06-19", "Juneteenth"], // USA Juneteenth
  ["2023-07-04", "Independence Day"], // USA Independence Day
  ["2023-09-04", "Labor Day"], // USA Labor Day
  ["2023-10-09", "Indigenous People's Day (Columbus Day)"], // USA Indig Peoples Day
];

type UseBusinessHoursHook = {
  // Whether it's business hours right now
  isDreamboundOpen: boolean;
  // If today is a holiday, the name of the holiday
  todayHoliday: string | null;
  // If tomorrow is a holiday, the name of the holiday
  tomorrowHoliday: string | null;
  // Start of business hours in browser timezone, like "7:00 AM"
  dreamboundBizHoursStartStr: string;
  // End of business hours in browser timezone, like "9:00 PM"
  dreamboundBizHoursEndStr: string;
  // Abbreviated browser timezone, like "PST"
  localTimezoneStr: string;
};

/**
 * Provide information for displaying whether Dreambound is open (calls are
 * taken, etc). This information is displayed in a timezone-aware format.
 */
export const useBusinessHours = (): UseBusinessHoursHook => {
  /*
  To determine whether it's during business hours, we work in the business-hours
  timezone and check that it falls between start & end.

  By bringing the browser to the business-hours timezone (and not the other way
  around) we avoid wrap-around issues.
   */
  const now = DateTime.now().setZone(BUSINESS_HOURS_TIMEZONE);
  const tomorrow = now.plus({ days: 1 });
  const bizStart = now.startOf("hour").set({ hour: BUSINESS_HOURS_START });
  const bizEnd = now.startOf("hour").set({ hour: BUSINESS_HOURS_END });

  const [, todayHoliday] = BUSINESS_HOURS_HOLIDAYS.find(([date]) => date === now.toISODate()) || [
    null,
    null,
  ];
  const [, tomorrowHoliday] = BUSINESS_HOURS_HOLIDAYS.find(
    ([date]) => date === tomorrow.toISODate()
  ) || [null, null];

  const isDreamboundOpen = !todayHoliday && bizStart < now && bizEnd > now;

  const dreamboundBizHoursStartStr = bizStart.toLocal().toLocaleString(DateTime.TIME_SIMPLE);
  const dreamboundBizHoursEndStr = bizEnd.toLocal().toLocaleString(DateTime.TIME_SIMPLE);
  const localTimezoneStr = bizStart.toLocal().toFormat("ZZZZ");

  return {
    isDreamboundOpen,
    todayHoliday,
    tomorrowHoliday,
    dreamboundBizHoursStartStr,
    dreamboundBizHoursEndStr,
    localTimezoneStr,
  };
};
